import { useEffect, useState, FC, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogActions, DialogTitle, IconButton, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { normalizeString } from 'utils/Validations';
import { PayRuleDB } from 'repository/timekeeper/@types/PayRuleModel';
import { TimekeeperService } from 'services/TimekeeperService';
import { generateRow, FixedRule, InterpretationRule, WorkRule, HolidayRule, Holiday, AssignmentRow, PayRuleFormData } from './common';
import { HolidayTabs } from 'repository/timekeeper/@types/HolidayModel';
import { PayRulesForm } from './PayRulesForm';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';

// Interfaces
interface EditPayRulesProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: PayRuleDB[];
    reloading: () => void;
    cleanSelected: () => void;
    rowsSelected: PayRuleDB[];
}

const timekeeperService = new TimekeeperService();

// Main Component
const EditPayRules: FC<EditPayRulesProps> = ({ open, setOpen, rows, reloading, cleanSelected, rowsSelected }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    // Tab state
    const [selectedTab, setSelectedTab] = useState(0);

    // Unified form data
    const [formData, setFormData] = useState<PayRuleFormData>({
        name: '',
        description: '',
        calculateProjectedTotals: false,
        tryPlannedTime: false,
        fixedRuleSelected: null,
        interpretationRuleSelected: null,
        workRuleSelected: null,
        creditHolidayRuleSelected: null,
        holidaysSelected: [],
        assignment: [generateRow(0)],
    });

    // Rule options
    const [fixedRules, setFixedRules] = useState<FixedRule[]>([]);
    const [interpretationRules, setInterpretationRules] = useState<InterpretationRule[]>([]);
    const [workRules, setWorkRules] = useState<WorkRule[]>([]);
    const [creditHolidayRules, setCreditHolidayRules] = useState<HolidayRule[]>([]);

    // Holidays
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loadingWorkRules, setLoadingWorkRules] = useState(false);

    // Alerts & Loadings
    const [alerts, setAlerts] = useState(false);
    const [loading, setLoading] = useState(false);

    // Initial data
    const [initialData, setInitialData] = useState<PayRuleDB | null>(null);

    //Errors
    const [showError, setShowError] = useState<{
        start_hour?: boolean;
        end_hour?: boolean;
        min_shift?: boolean;
        max_shift?: boolean;
    }>({});

    // validate time inputs
    useEffect(() => {
        const timeout = setTimeout(() => {
            const newErrors: { [key: string]: boolean } = {};
            const data = formData.assignment.reduce(
                (acc, row) => {
                    acc.start_hour = acc.start_hour || row.start_hour;
                    acc.end_hour = acc.end_hour || row.end_hour;
                    acc.min_shift = acc.min_shift || row.min_shift;
                    acc.max_shift = acc.max_shift || row.max_shift;
                    return acc;
                },
                { start_hour: '', end_hour: '', min_shift: '', max_shift: '' },
            );

            for (const [key, value] of Object.entries(data)) {
                if (value.length > 0 && value.length < 5) {
                    newErrors[key] = true;
                }
            }
            setShowError(newErrors);
        }, 500);

        return () => clearTimeout(timeout);
    }, [formData.assignment]);

    // Memoized values
    const existName = useCallback(
        (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row) => row.pay_rule_id !== rowsSelected[0]?.pay_rule_id && normalizeString(row.name) === normalizedInput);
        },
        [rows, rowsSelected],
    );

    const hasChanges = useMemo(() => {
        if (!initialData) return false;

        const orderedData = formData.assignment.map((row) => {
            const { id, ...data } = row;
            return { ...data, days_week: Array.from(row.days_week) };
        });

        return (
            formData.name !== initialData.name ||
            formData.description !== initialData.description ||
            formData.calculateProjectedTotals !== initialData.calculate_projected_totals ||
            formData.tryPlannedTime !== initialData.treat_planned_hour ||
            formData.fixedRuleSelected?.fixed_rule_id !== initialData.fixed_rule_id ||
            formData.interpretationRuleSelected?.interpretation_rule_id !== initialData.interpretation_rule_id ||
            formData.workRuleSelected?.work_rule_id !== initialData.work_rule_default_id ||
            formData.creditHolidayRuleSelected?.holiday_rule_id !== initialData.credit_holiday_default_id ||
            JSON.stringify(formData.holidaysSelected.sort()) !== JSON.stringify(initialData.holidays.sort()) ||
            JSON.stringify(orderedData) !== JSON.stringify(initialData.asignation)
        );
    }, [formData, initialData]);

    const isDisabled = useMemo(() => {
        return (
            formData.name === '' ||
            existName(formData.name) ||
            showError.start_hour ||
            showError.end_hour ||
            showError.min_shift ||
            showError.max_shift ||
            !formData.fixedRuleSelected ||
            !formData.interpretationRuleSelected ||
            !formData.workRuleSelected ||
            !hasChanges
        );
    }, [formData.name, formData.fixedRuleSelected, formData.interpretationRuleSelected, formData.workRuleSelected, existName, showError, hasChanges]);

    const closeForm = useCallback(() => {
        setFormData({
            name: '',
            description: '',
            calculateProjectedTotals: false,
            tryPlannedTime: false,
            fixedRuleSelected: null,
            interpretationRuleSelected: null,
            workRuleSelected: null,
            creditHolidayRuleSelected: null,
            holidaysSelected: [],
            assignment: [generateRow(0)],
        });
        setFixedRules([]);
        setInterpretationRules([]);
        setWorkRules([]);
        setCreditHolidayRules([]);
        setHolidays([]);
        setLoading(false);
        setSelectedTab(0);
        setAlerts(false);
        setInitialData(null);
        setOpen(false);
        cleanSelected();
    }, [setOpen, cleanSelected]);

    const handleClose = useCallback(() => {
        if (hasChanges) {
            setAlerts(true);
        } else {
            closeForm();
        }
    }, [hasChanges, closeForm]);

    const updatePayRule = useCallback(async () => {
        setLoading(true);
        const orderedData = formData.assignment.map((row) => {
            const { id, ...data } = row;
            return { ...data, days_week: Array.from(row.days_week) };
        });

        const body = {
            name: formData.name,
            description: formData.description,
            interpretation_rule_id: formData.interpretationRuleSelected?.interpretation_rule_id || 0,
            credit_holiday_default_id: formData.creditHolidayRuleSelected?.holiday_rule_id,
            fixed_rule_id: formData.fixedRuleSelected?.fixed_rule_id,
            work_rule_default_id: formData.workRuleSelected?.work_rule_id || 0,
            calculate_projected_totals: formData.calculateProjectedTotals,
            treat_planned_hour: formData.tryPlannedTime,
            holidays: formData.holidaysSelected,
            asignation: orderedData.length === 1 && orderedData[0].pay_rule_id === null ? [] : orderedData,
        };

        try {
            await timekeeperService.payrules.updatePayRule(rowsSelected[0]?.pay_rule_id, body);
            enqueueSnackbar(t('settings.pay_rules.dialog.alerts.edit.success'), { variant: 'success' });
            reloading();
            closeForm();
        } catch (error) {
            console.error('Error al actualizar la regla de pago:', error);
            enqueueSnackbar(t('settings.pay_rules.dialog.alerts.edit.error'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [t, enqueueSnackbar, reloading, closeForm, rowsSelected, formData]);

    // Effects
    useEffect(() => {
        if (!open || !rowsSelected || rowsSelected.length === 0) return;

        const selectedPayRule = rowsSelected[0];

        const fetchData = async () => {
            setLoadingWorkRules(true);
            try {
                const [fixedRulesData, interpretationRulesData, workRulesData, creditHolidayRulesData, holidaysData] = await Promise.allSettled([
                    timekeeperService.fixed_rule.getFixedRules(),
                    timekeeperService.interpretation_rules.getAll(),
                    timekeeperService.work_rules.getWorkRules(),
                    timekeeperService.credit_holiday_rule.getCreditHolidayRules(),
                    timekeeperService.holiday.getHolidays(),
                ]);

                if (fixedRulesData.status === 'fulfilled') {
                    const rules = fixedRulesData.value || [];
                    const orderedRules = rules.sort((a: FixedRule, b: FixedRule) => a.name.localeCompare(b.name));
                    setFixedRules(orderedRules);

                    const selectedRule = orderedRules.find((r: FixedRule) => r.fixed_rule_id === selectedPayRule.fixed_rule_id);
                    if (selectedRule) {
                        setFormData((prev) => ({ ...prev, fixedRuleSelected: selectedRule }));
                    }
                }

                if (interpretationRulesData.status === 'fulfilled') {
                    const rules = interpretationRulesData.value || [];
                    const orderedRules = rules.sort((a: InterpretationRule, b: InterpretationRule) => a.name.localeCompare(b.name));
                    setInterpretationRules(orderedRules);

                    const selectedRule = orderedRules.find((r: InterpretationRule) => r.interpretation_rule_id === selectedPayRule.interpretation_rule_id);
                    if (selectedRule) {
                        setFormData((prev) => ({ ...prev, interpretationRuleSelected: selectedRule }));
                    }
                }

                if (workRulesData.status === 'fulfilled') {
                    const rules = workRulesData.value || [];
                    const orderedRules = rules.sort((a: WorkRule, b: WorkRule) => a.name.localeCompare(b.name));
                    setWorkRules(orderedRules);

                    const selectedRule = orderedRules.find((r: WorkRule) => r.work_rule_id === selectedPayRule.work_rule_default_id);
                    if (selectedRule) {
                        setFormData((prev) => ({ ...prev, workRuleSelected: selectedRule }));
                    }
                }

                if (creditHolidayRulesData.status === 'fulfilled') {
                    const rules = creditHolidayRulesData.value || [];
                    const orderedRules = rules.sort((a: HolidayRule, b: HolidayRule) => a.name.localeCompare(b.name));
                    setCreditHolidayRules(orderedRules);

                    if (selectedPayRule.credit_holiday_default_id) {
                        const selectedRule = orderedRules.find((r: HolidayRule) => r.holiday_rule_id === selectedPayRule.credit_holiday_default_id);
                        if (selectedRule) {
                            setFormData((prev) => ({ ...prev, creditHolidayRuleSelected: selectedRule }));
                        }
                    }
                }

                if (holidaysData.status === 'fulfilled') {
                    const holidayRows: HolidayTabs[] = holidaysData.value.map((holiday: { holiday_id: number; name: string; country?: string }) => ({
                        id: holiday.holiday_id,
                        name: holiday.name,
                        option: false,
                        holiday_credit_rule: '',
                        country: holiday.country || '',
                    }));
                    const orderedHolidayRows = holidayRows.sort((a, b) => a.name.localeCompare(b.name));
                    setHolidays(orderedHolidayRows);
                }

                // Precargar datos desde selectedPayRule (primer elemento)
                const assignmentRows: AssignmentRow[] =
                    selectedPayRule.asignation && selectedPayRule.asignation.length > 0
                        ? selectedPayRule.asignation.map((item, index) => ({
                              id: index,
                              order: item.order,
                              pay_rule_id: item.pay_rule_id,
                              work_rule_id: item.work_rule_id,
                              start_hour: item.start_hour || '',
                              end_hour: item.end_hour || '',
                              min_shift: item.min_shift || '',
                              max_shift: item.max_shift || '',
                              days_week: new Set(item.days_week || []),
                              planned: item.planned,
                              unplanned: item.unplanned,
                              pay_rule_r_work_rule_id: item.pay_rule_r_work_rule_id,
                          }))
                        : [generateRow(0)];

                const holidaysIds = Array.isArray(selectedPayRule.holidays) ? selectedPayRule.holidays.map((h: any) => (typeof h === 'number' ? h : h.holiday_id)) : [];

                setFormData((prev) => ({
                    ...prev,
                    name: selectedPayRule.name,
                    description: selectedPayRule.description,
                    calculateProjectedTotals: selectedPayRule.calculate_projected_totals,
                    tryPlannedTime: selectedPayRule.treat_planned_hour,
                    holidaysSelected: holidaysIds,
                    assignment: assignmentRows,
                }));

                // Guardar datos iniciales para comparación
                setInitialData({
                    id: selectedPayRule.id,
                    pay_rule_id: selectedPayRule.pay_rule_id,
                    name: selectedPayRule.name,
                    description: selectedPayRule.description,
                    interpretation_rule_id: selectedPayRule.interpretation_rule_id,
                    credit_holiday_default_id: selectedPayRule.credit_holiday_default_id,
                    fixed_rule_id: selectedPayRule.fixed_rule_id,
                    work_rule_default_id: selectedPayRule.work_rule_default_id,
                    calculate_projected_totals: selectedPayRule.calculate_projected_totals,
                    treat_planned_hour: selectedPayRule.treat_planned_hour,
                    holidays: holidaysIds,
                    asignation: selectedPayRule.asignation || [],
                });
            } catch (error) {
                console.error('Error fetching rules:', error);
            } finally {
                setLoadingWorkRules(false);
            }
        };

        fetchData();
    }, [open, rowsSelected]);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') return;
                handleClose();
            }}
            maxWidth={selectedTab === 0 ? 'sm' : selectedTab === 1 ? 'md' : 'xl'}
            fullWidth
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('settings.pay_rules.dialog.titles.edit')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>{alerts && <AlertCustom alerts={alerts} setAlerts={setAlerts} closeForm={closeForm} t={t} />}</Box>

            <DialogContent dividers>
                <PayRulesForm
                    formData={formData}
                    setFormData={setFormData}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    ruleOptions={{
                        fixedRules,
                        interpretationRules,
                        workRules,
                        creditHolidayRules,
                    }}
                    holidays={holidays}
                    loadingWorkRules={loadingWorkRules}
                    existName={existName}
                    isEditMode={true}
                />
            </DialogContent>

            <DialogActions>
                <Button variant="contained" color="error" onClick={handleClose}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={updatePayRule} disabled={isDisabled || loading} loading={loading}>
                    {t('general.buttons.edit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { EditPayRules };
